<template>
  <ul class="list">
    <li
      v-for="locale in localesWithUrls"
      :key="locale.code"
      :class="{ selected: isLanguageSelected(locale) }"
    >
      <a
        :href="locale.url"
        @click.prevent="switchLanguage(locale.code)"
        :class="{ desktop: $isDesktop }"
      >
        <span class="locale-flag">
          <lazy-pu-flag :country-iso="locale.flag" />
        </span>
        <span class="locale-name">
          {{ locale.name }}
        </span>
      </a>
    </li>
  </ul>
</template>

<script setup lang="ts">
import { useNuxtApp, useRouter } from 'nuxt/app'
import { computed } from 'vue'
import { getLanguageUrl } from '~/lang/utils/redirect'
import { useUserDataStore } from '~/stores/useUserDataStore'
import { ensureArray } from '~/utility/array'
import { getPriceParamsFromQuery } from '~/utility/getPriceParamsFromQuery'
import { logger } from '../../../utility/logger'

const router = useRouter()
const currentRoute = computed(() => router.currentRoute.value)
const { $i18n, $gtm } = useNuxtApp()
const userDataStore = useUserDataStore(usePinia())
const localesList = computed(() => $i18n.locales)

const redirectUrl = computed(() => {
  const queryParams = new URLSearchParams()
  const query = {
    ...getPriceParamsFromQuery(currentRoute.value.query),
    page: undefined,
    pitchtype: undefined,
    sa: undefined,
    ved: undefined,
    hierarchyPath: undefined,
  }
  Object.entries(query).forEach(([key, value]) => {
    for (const v of ensureArray(value)) {
      if (v && typeof v === 'string') {
        queryParams.append(key, v)
      }
    }
  })
  const queryParamsString = queryParams.toString()
  return queryParamsString
    ? `${currentRoute.value.path}?${queryParamsString}`
    : currentRoute.value.path
})

const localesWithUrls = computed(() => {
  return localesList.value.map((locale) => ({
    ...locale,
    url: getLanguageUrl(redirectUrl.value, locale.code),
  }))
})

const switchLanguage = async (localeCode: string) => {
  logger('switching language', localeCode)
  $gtm.push({
    event: 'language-switch',
    toLocale: localeCode,
    fromLocale: $i18n.locale,
  })

  await userDataStore.setLanguage(localeCode)
}

const isLanguageSelected = (locale: { code: string }) => {
  return locale.code === $i18n.locale
}
</script>

<style scoped>
a {
  align-items: center;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  text-decoration: none;
  width: 100%;
}

a.desktop {
  padding: 0.7em 1em;
}

.locale-flag {
  max-width: 3em;
  margin-right: 0.8em;
  flex-grow: 1;
  text-align: right;
}

.locale-name {
  flex-grow: 1;
}

.locale-flag img {
  height: auto;
  width: 2em;
  max-width: none;
  vertical-align: middle;
}
</style>
